/**
 * useComposerMentionChips — the React half of the draft box's mention chips
 * (Miles, 2026-09-22). The decision and the DOM work live in
 * services/threadMentions/composerChipSync.ts, which is where the reasoning
 * is written and where the tests drive it; this is the policy around it.
 *
 * Three guards, and each one is a real case rather than defensive habit:
 *
 *  · NOT FOCUSED ⇒ nothing. Editing a composer the person is not in would
 *    move a caret that is somewhere else entirely — another composer, a
 *    search box, the page.
 *  · RE-ENTRANT ⇒ nothing. `execCommand` fires a native input event
 *    synchronously, which is this handler again; the sync already re-reads
 *    the DOM after every edit, so the nested call has nothing to do and is
 *    refused rather than allowed to recurse one level per chip.
 *  · NO RESOLVER ⇒ nothing, at zero cost. That is every solo thread, and it
 *    is also the instant-off: the chips exist exactly where the transcript's
 *    chips and the draft pills already do, because all three read the one
 *    resolver.
 */
import { useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import { syncComposerChips } from '../../services/threadMentions/composerChipSync';
import type { MentionChipPerson } from '../../services/threadMentions/mentionChip';

/**
 * Whether the chip pass may run for THIS input event.
 *
 * Two events must be left alone, and neither is hypothetical:
 *
 *  · MID-COMPOSITION (`isComposing`). An IME — Chinese, Japanese, Korean,
 *    and every phone's autocorrect — holds an open composition in the
 *    composer while the person picks characters. Editing the DOM and moving
 *    the selection underneath it cancels that composition and can drop the
 *    characters being typed. An earlier handle in the draft can become
 *    eligible while a composition is open somewhere else, so this is reachable
 *    without doing anything unusual; the chip forms on the next ordinary
 *    keystroke instead.
 *
 *  · UNDO AND REDO (`historyUndo` / `historyRedo`). Chipping goes through
 *    `execCommand`, so it IS on the browser's undo stack — Ctrl+Z un-chips,
 *    which is right. Without this the pass would immediately re-chip the
 *    handle the undo just restored, and undo would look broken.
 *
 * Read from the native event rather than tracked as state: the answer is a
 * property of the keystroke, and a flag set by compositionstart/end is one
 * missed event away from wedging the feature off.
 */
export function chipsMayRunOn(event: Event | null | undefined): boolean {
  const e = event as (InputEvent & { isComposing?: boolean }) | null | undefined;
  if (!e) return true;
  if (e.isComposing) return false;
  return !String(e.inputType ?? '').startsWith('history');
}

export interface ComposerMentionChipsOptions {
  inputRef: RefObject<HTMLDivElement | null>;
  /** The same resolver the transcript and the pill overlay read. Null ⇒ inert. */
  resolve: ((handle: string) => MentionChipPerson | null) | null;
  /** Instant-off. False ⇒ this hook does nothing at all, at zero cost. */
  enabled: boolean;
}

export function useComposerMentionChips(
  opts: ComposerMentionChipsOptions,
): { sync: () => void } {
  const { inputRef, resolve, enabled } = opts;
  const running = useRef(false);

  const sync = useCallback(() => {
    if (!enabled || !resolve) return;
    const el = inputRef.current;
    if (!el || running.current) return;
    if (typeof document === 'undefined' || document.activeElement !== el) return;
    running.current = true;
    try {
      syncComposerChips(el, resolve);
    } catch {
      // A draft that cannot be chipped is still a draft that sends correctly.
      // Never let decoration take the composer down.
    } finally {
      running.current = false;
    }
  }, [enabled, resolve, inputRef]);

  return { sync };
}
